import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import { X, MessageCircle, Phone, Save, Eye, EyeOff, Type, Bold, Italic, List, Mic, MicOff } from 'lucide-react';
import { FileUploadWithPreview } from './FileUploadWithPreview';
import { ProgressDropdown, ProgressStatus as DropdownProgressStatus } from './ProgressDropdown';
import { transcriptionService } from '../../lib/transcriptionService';
import { cameraService } from '../../utils/camera';
import { logCompatibilityInfo, getCompatibilityMessage, detectBrowser } from '../../utils/browserCompat';
import Swal from 'sweetalert2';
import { useLanguage } from '../../contexts/LanguageContext';

// Progress status options
export type ProgressStatus = 'Visit' | 'Presentation' | 'Trial' | 'Rejected' | 'MOU Signed' | null;

interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (notes: string, actionType?: 'whatsapp' | 'call', files?: File[], markedActionType?: 'whatsapp' | 'call' | null, progress?: ProgressStatus) => void;
  title: string;
  placeholder?: string;
  initialValue?: string;
  actionType?: 'whatsapp' | 'call';
  customerName?: string;
  phoneNumber?: string;
  showActionSelection?: boolean;
  enableFileUpload?: boolean;
  enableProgressSelection?: boolean; // New prop for showing progress dropdown
  initialProgress?: ProgressStatus; // Initial progress value
  onActionSelect?: (actionType: 'whatsapp' | 'call', notes: string, files?: File[]) => Promise<boolean>;
  // New props for direct WhatsApp/Call functionality
  customerPhoneNumbers?: string[]; // Array of customer phone numbers
  onDirectContact?: (actionType: 'whatsapp' | 'call', phoneNumber: string, shouldMark: boolean) => Promise<void>;
}

export const NotesModal: FC<NotesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  placeholder,
  initialValue = "",
  actionType,
  customerName,
  phoneNumber,
  showActionSelection = false,
  enableFileUpload = false,
  enableProgressSelection = false,
  initialProgress = null,
  onActionSelect,
  customerPhoneNumbers = [],
  onDirectContact
}) => {
  const { t } = useLanguage();
  const [notes, setNotes] = useState(initialValue);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'whatsapp' | 'call' | null>(actionType || null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileUploadResetKey, setFileUploadResetKey] = useState<number>(0);
  const [selectedProgress, setSelectedProgress] = useState<ProgressStatus>(initialProgress);

  // Convert between ProgressStatus types for compatibility
  const convertToDropdownProgress = (status: ProgressStatus): DropdownProgressStatus | null => {
    switch (status) {
      case 'Visit': return 'visit';
      case 'Presentation': return 'presentation';
      case 'Trial': return 'trial';
      case 'Rejected': return 'rejected';
      case 'MOU Signed': return 'mou-signed';
      default: return null;
    }
  };

  const convertFromDropdownProgress = (status: DropdownProgressStatus): ProgressStatus => {
    switch (status) {
      case 'visit': return 'Visit';
      case 'presentation': return 'Presentation';
      case 'trial': return 'Trial';
      case 'rejected': return 'Rejected';
      case 'mou-signed': return 'MOU Signed';
      default: return null as any;
    }
  };
  const textareaRef = useRef<any>(null);

  // State for phone number selection and marking
  const [showPhoneSelection, setShowPhoneSelection] = useState(false);
  const [, setSelectedPhoneNumber] = useState<string>('');
  const [pendingActionType, setPendingActionType] = useState<'whatsapp' | 'call' | null>(null);
  const [markedPhoneNumbers, setMarkedPhoneNumbers] = useState<string[]>([]);
  // NEW: Track which action type was marked (for setting interaction_type on save)
  const [markedActionType, setMarkedActionType] = useState<'whatsapp' | 'call' | null>(null);

  // Transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  // Browser compatibility state
  const [isTranscriptionSupported, setIsTranscriptionSupported] = useState(true);
  const [compatibilityMessage, setCompatibilityMessage] = useState('');

  // Reset notes and files when modal opens
  useEffect(() => {
    if (isOpen) {
      setNotes(initialValue);
      setIsPreviewMode(false);
      setUploadedFiles([]); // CRITICAL FIX: Reset uploaded files when modal opens
      setFileUploadResetKey(prev => prev + 1); // Trigger FileUploadWithPreview reset
      setSelectedProgress(initialProgress); // Reset progress to initial value
      // Reset phone marking states
      setMarkedPhoneNumbers([]);
      setMarkedActionType(null);
      setShowPhoneSelection(false);
      setPendingActionType(null);
      setSelectedPhoneNumber('');
      console.log('🔄 NotesModal reset on open:', {
        initialValue,
        initialProgress,
        resetFiles: true,
        resetPhoneStates: true,
        newResetKey: fileUploadResetKey + 1,
        customerPhoneNumbers: customerPhoneNumbers,
        phoneNumbersCount: customerPhoneNumbers?.length || 0,
        showActionSelection: showActionSelection,
        enableProgressSelection: enableProgressSelection,
        shouldShowQuickContact: customerPhoneNumbers && customerPhoneNumbers.length > 0 && !showActionSelection
      });
      // Focus textarea after modal animation
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, initialValue, initialProgress, showActionSelection, enableProgressSelection]); // REMOVE customerPhoneNumbers to prevent infinite loop

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Recording timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRecording]);

  // Browser compatibility check effect
  useEffect(() => {
    const checkTranscriptionCompatibility = async () => {
      try {
        // Log browser compatibility info for debugging
        logCompatibilityInfo();

        // Check browser support
        const browserInfo = detectBrowser();
        const compatMessage = getCompatibilityMessage();

        if (!browserInfo.isSupported) {
          setIsTranscriptionSupported(false);
          setCompatibilityMessage(compatMessage);
          console.warn('🎤 Transcription not supported:', {
            browser: `${browserInfo.name} ${browserInfo.version}`,
            missingFeatures: browserInfo.missingFeatures,
            message: compatMessage
          });
        } else {
          // Additional check for camera service support
          const cameraSupported = await cameraService.checkCameraSupport();
          if (!cameraSupported) {
            setIsTranscriptionSupported(false);
            setCompatibilityMessage('Your device does not support audio recording. Please use a modern browser with microphone access.');
            console.warn('🎤 Camera service not supported');
          } else {
            setIsTranscriptionSupported(true);
            setCompatibilityMessage('');
            console.log('✅ Transcription fully supported');
          }
        }
      } catch (error) {
        console.error('Error checking transcription compatibility:', error);
        setIsTranscriptionSupported(false);
        setCompatibilityMessage('Unable to check browser support. Transcription features may not work.');
      }
    };

    if (isOpen) {
      checkTranscriptionCompatibility();
    }
  }, [isOpen]);

  const handleSave = useCallback(async () => {
    // DEBUGGING: Log NotesModal handleSave
    console.log('📝 NotesModal handleSave called with:', {
      notesLength: notes.trim().length,
      selectedAction: selectedAction || 'none',
      uploadedFilesCount: uploadedFiles.length,
      uploadedFiles: uploadedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
      hasFiles: uploadedFiles.length > 0,
      showActionSelection,
      markedActionType: markedActionType,
      markedPhoneNumbers: markedPhoneNumbers,
      selectedProgress: selectedProgress // Debug Progress value
    });

    try {
      // If showActionSelection is false, directly save without action selection
      if (!showActionSelection) {
        // CRITICAL FIX: Save files to local variable to prevent state reset issues
        const filesToUpload = uploadedFiles.length > 0 ? [...uploadedFiles] : undefined;
        const fileCount = uploadedFiles.length;

        console.log('💾 Saving files to local variable before async operations:', {
          fileCount,
          files: filesToUpload?.map(f => ({ name: f.name, size: f.size, type: f.type })),
          selectedProgress,
          filesToUploadLength: filesToUpload?.length || 0
        });

        // Pass markedActionType and selectedProgress to parent
        // IMPORTANT: Pass filesToUpload (local copy) instead of uploadedFiles (state)
        onSave(
          notes.trim(),
          selectedAction || undefined,
          filesToUpload,
          markedActionType,
          selectedProgress
        );

        // Show success notification with action type info and completion status
        // Use fileCount instead of uploadedFiles.length
        const successText = [
          t('notes.save_success'),
          fileCount > 0 ? t('notes.files_saved', { count: fileCount }) : '',
          markedActionType ? t('notes.interaction_type_set', { type: markedActionType === 'whatsapp' ? t('notes.whatsapp') : t('notes.call') }) : '',
          '✅ ' + t('notes.interaction_completed')
        ].filter(Boolean).join(' ');

        await Swal.fire({
          icon: 'success',
          title: t('notes.completed'),
          text: successText,
          confirmButtonText: t('notes.great'),
          confirmButtonColor: 'rgb(var(--status-success))',
          timer: 3500,
          timerProgressBar: true
        });

        onClose();
        return;
      }

      // Original logic for when showActionSelection is true
      const filesToUpload = uploadedFiles.length > 0 ? [...uploadedFiles] : undefined;
      onSave(notes.trim(), selectedAction || undefined, filesToUpload, markedActionType, selectedProgress);
      onClose();

    } catch (error) {
      console.error('Failed to save notes:', error);

      // Show error notification
      await Swal.fire({
        icon: 'error',
        title: t('notes.save_failed'),
        text: t('notes.save_failed_message'),
        confirmButtonText: t('notes.ok'),
        confirmButtonColor: 'hsl(var(--destructive))'
      });
    }
  }, [notes, selectedAction, uploadedFiles, onSave, onClose, showActionSelection, markedActionType, markedPhoneNumbers, selectedProgress]);

  const handleFileUpload = useCallback(async (files: File[], metadata?: { [fileName: string]: any }) => {
    // DEBUGGING: Log file upload
    console.log('📁 NotesModal handleFileUpload called with:', {
      newFilesCount: files.length,
      newFiles: files.map(f => ({ name: f.name, size: f.size, type: f.type })),
      currentFilesCount: uploadedFiles.length,
      hasMetadata: !!metadata,
      metadataFilesCount: metadata ? Object.keys(metadata).length : 0
    });

    // Log metadata details if available
    if (metadata) {
      console.log('📊 File metadata received:', Object.keys(metadata).map(fileName => ({
        fileName,
        hasGPS: !!metadata[fileName]?.metadata?.gpsCoordinates,
        dataQuality: metadata[fileName]?.metadata?.dataQuality,
        extractionTime: metadata[fileName]?.extractionTime
      })));
    }

    // Store files and metadata for batch upload when saving
    setUploadedFiles(prev => {
      const updated = [...prev, ...files];
      console.log('📁 Updated uploadedFiles STATE:', {
        previousCount: prev.length,
        newTotalCount: updated.length,
        allFiles: updated.map(f => ({ name: f.name, size: f.size, type: f.type }))
      });
      return updated;
    });

    // Store metadata separately if provided
    if (metadata) {
      // Note: In a real implementation, you might want to store this in state
      // for use when saving the notes with attachments
      console.log('📊 Storing metadata for later use with notes:', Object.keys(metadata));
    }

    // Verify state update
    setTimeout(() => {
      console.log('📁 VERIFICATION - uploadedFiles state after update:', uploadedFiles.length);
    }, 100);

    return Promise.resolve();
  }, [uploadedFiles.length]);

  const handleActionClick = useCallback(async (actionType: 'whatsapp' | 'call') => {
    // DEBUGGING: Log action click
    console.log('🎯 NotesModal handleActionClick called with:', {
      actionType,
      notesLength: notes.trim().length,
      uploadedFilesCount: uploadedFiles.length,
      uploadedFiles: uploadedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
      hasOnActionSelect: !!onActionSelect
    });

    // Set selected action
    setSelectedAction(actionType);

    // If onActionSelect callback is provided, call it immediately
    if (onActionSelect) {
      try {
        const shouldProceed = await onActionSelect(actionType, notes.trim(), uploadedFiles.length > 0 ? uploadedFiles : undefined);
        console.log('🎯 onActionSelect returned:', shouldProceed);
        if (shouldProceed) {
          // If user confirmed, close modal automatically
          onClose();
        }
      } catch (error) {
        console.error('Action selection failed:', error);
      }
    }
  }, [notes, uploadedFiles, onActionSelect, onClose]);

  // Handle direct contact button click (WhatsApp or Call)
  const handleDirectContact = useCallback(async (actionType: 'whatsapp' | 'call') => {
    // Filter out valid phone numbers (not empty and not just spaces)
    const validPhoneNumbers = customerPhoneNumbers.filter(phone =>
      phone && phone.trim().length > 0 && phone.trim() !== '-' && phone.trim() !== 'N/A'
    );

    console.log('📞 Direct contact requested:', {
      actionType,
      totalPhoneNumbers: customerPhoneNumbers.length,
      validPhoneNumbers: validPhoneNumbers.length,
      validNumbers: validPhoneNumbers
    });

    if (validPhoneNumbers.length === 0) {
      await Swal.fire({
        icon: 'warning',
        title: t('notes.no_phone_number'),
        text: t('notes.no_valid_phone_numbers'),
        confirmButtonText: t('notes.ok'),
        confirmButtonColor: 'hsl(var(--primary))'
      });
      return;
    }

    // If only one number, show Mark/Contact options directly
    if (validPhoneNumbers.length === 1) {
      const phoneNumber = validPhoneNumbers[0];
      await showMarkContactOptions(actionType, phoneNumber);
      return;
    }

    // If multiple numbers, show phone selection popup
    setPendingActionType(actionType);
    setShowPhoneSelection(true);
  }, [customerPhoneNumbers]);

  // Show Mark vs Contact options
  const showMarkContactOptions = useCallback(async (actionType: 'whatsapp' | 'call', phoneNumber: string) => {
    const actionLabel = actionType === 'whatsapp' ? t('notes.whatsapp') : t('notes.call');
    const formatPhoneForDisplay = (phone: string) => {
      const cleaned = phone.replace(/[^\d+]/g, '');
      if (cleaned.startsWith('+62')) {
        return cleaned.replace('+62', '+62 ').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
      return cleaned;
    };

    const result = await Swal.fire({
      title: t('notes.contact_action', { action: actionLabel }),
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <p><strong>${t('notes.customer')}:</strong> ${customerName || t('notes.unknown_customer')}</p>
          <p><strong>${t('notes.number')}:</strong> ${formatPhoneForDisplay(phoneNumber)}</p>
          <div style="margin: 20px 0; padding: 15px; background-color: hsl(var(--muted)); border-radius: 8px;">
            <p style="margin: 0; font-size: 14px; color: #666;">${t('notes.choose_what_to_do')}</p>
          </div>
        </div>
      `,
      showCancelButton: true,
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: t('notes.contact_via', { actionType: actionLabel }),
      denyButtonText: t('notes.mark_only'),
      cancelButtonText: t('notes.cancel'),
      confirmButtonColor: actionType === 'whatsapp' ? 'rgb(var(--service-whatsapp))' : 'hsl(var(--primary))',
      denyButtonColor: 'hsl(var(--muted-foreground))',
      cancelButtonColor: 'rgb(var(--status-error))'
    });

    if (result.isConfirmed) {
      // Contact - proceed to WhatsApp/Call
      if (onDirectContact) {
        await onDirectContact(actionType, phoneNumber, false);
      }
    } else if (result.isDenied) {
      // Mark only - add to marked numbers and set marked action type
      setMarkedPhoneNumbers(prev => {
        if (!prev.includes(phoneNumber)) {
          const updated = [...prev, phoneNumber];
          console.log('📝 Phone number marked:', {
            phoneNumber,
            actionType,
            totalMarked: updated.length,
            markedNumbers: updated
          });

          // Set the marked action type for interaction_type on save
          setMarkedActionType(actionType);
          console.log('🎯 Marked action type set:', {
            markedActionType: actionType,
            willSetInteractionType: actionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call'
          });

          // Show success message
          Swal.fire({
            icon: 'success',
            title: t('notes.number_marked'),
            text: t('notes.marked_for', { number: formatPhoneForDisplay(phoneNumber), actionType: actionType === 'whatsapp' ? t('notes.whatsapp') : t('notes.call') }),
            confirmButtonText: t('notes.ok'),
            confirmButtonColor: 'hsl(var(--primary))',
            timer: 2000,
            timerProgressBar: true
          });

          return updated;
        }
        return prev;
      });
    }
  }, [customerName, onDirectContact]);

  // Handle phone number selection from popup
  const handlePhoneSelection = useCallback(async (phoneNumber: string) => {
    setSelectedPhoneNumber(phoneNumber);
    setShowPhoneSelection(false);

    if (pendingActionType) {
      await showMarkContactOptions(pendingActionType, phoneNumber);
      setPendingActionType(null);
    }
  }, [pendingActionType, showMarkContactOptions]);

  // Format recording time
  const formatRecordingTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Start audio recording for transcription
  const handleStartRecording = async () => {
    // Check compatibility before starting
    if (!isTranscriptionSupported) {
      await Swal.fire({
        icon: 'warning',
        title: t('notes.feature_not_supported'),
        text: compatibilityMessage || t('notes.browser_not_supported'),
        confirmButtonText: t('notes.ok'),
        confirmButtonColor: 'hsl(var(--accent))'
      });
      return;
    }

    try {
      setIsRecording(true);
      await cameraService.startAudioRecording();
    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsRecording(false);

      // Enhanced error message based on compatibility
      let errorMessage = t('notes.recording_error');
      if (error instanceof Error) {
        if (error.message.includes('NotAllowedError')) {
          errorMessage = t('notes.microphone_denied');
        } else if (error.message.includes('NotFoundError')) {
          errorMessage = t('notes.no_microphone');
        } else if (error.message.includes('NotSupportedError')) {
          errorMessage = t('notes.recording_not_supported');
        }
      }

      await Swal.fire({
        icon: 'error',
        title: t('notes.recording_failed'),
        text: errorMessage,
        confirmButtonText: t('notes.ok'),
        confirmButtonColor: 'hsl(var(--destructive))'
      });
    }
  };

  // Stop recording and transcribe
  const handleStopRecordingAndTranscribe = async () => {
    try {
      setIsRecording(false);
      setIsTranscribing(true);

      // Stop recording and get audio data
      const audioData = await cameraService.stopAudioRecording();

      // Transcribe the audio using client-side OpenAI Whisper

      if (!transcriptionService.isAvailable()) {
        throw new Error('OpenAI API key not configured. Please set VITE_OPENAI_API_KEY in your .env file and restart the dev server.');
      }

      const transcriptionResult = await transcriptionService.transcribeAudio(audioData.blob, {
        language: 'id', // Indonesian
        prompt: 'Customer interaction notes, conversation details, business discussion',
        response_format: 'text',
        temperature: 0
      });

      if (transcriptionResult.error) {
        throw new Error(transcriptionResult.error);
      }

      // Add transcribed text to notes with timestamp
      const { formatTranscriptionForVisitNotes } = await import('../../utils/transcriptionHelpers');
      const transcribedText = transcriptionResult.transcription.trim();
      const newNotes = formatTranscriptionForVisitNotes(notes || '', transcribedText);

      setNotes(newNotes);

      // Show success message
      await Swal.fire({
        icon: 'success',
        title: t('notes.transcription_complete'),
        text: t('notes.transcription_complete_message'),
        confirmButtonText: t('notes.great'),
        confirmButtonColor: 'hsl(var(--primary))',
        timer: 3000,
        timerProgressBar: true
      });

    } catch (error) {
      console.error('Transcription failed:', error);

      await Swal.fire({
        icon: 'error',
        title: t('notes.transcription_failed'),
        text: error instanceof Error ? error.message : t('notes.transcription_error'),
        confirmButtonText: t('notes.ok'),
        confirmButtonColor: 'hsl(var(--destructive))'
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  // Toggle recording (start/stop)
  const handleToggleRecording = () => {
    if (isRecording) {
      handleStopRecordingAndTranscribe();
    } else {
      handleStartRecording();
    }
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
  }, [onClose, handleSave]);

  // Markdown helper functions
  const insertMarkdown = useCallback((before: string, after: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    setNotes(newText);

    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, []);

  // Simple markdown to HTML conversion for preview
  const renderMarkdown = useCallback((text: string) => {
    let html = text
      // Headers
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')
      // Bold and italic
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
      // Lists (simple implementation)
      .replace(/^[*\-+] (.+)$/gm, '<li class="ml-4">• $1</li>')
      // Line breaks
      .replace(/\n/g, '<br>');

    return html;
  }, []);

  const getActionIcon = () => {
    switch (actionType) {
      case 'whatsapp':
        return <MessageCircle className="w-5 h-5 text-green-500" />;
      case 'call':
        return <Phone className="w-5 h-5 text-primary" />;
      default:
        return <Type className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const formatPhoneNumber = (number?: string) => {
    if (!number) return '';
    const cleaned = number.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+62')) {
      return cleaned.replace('+62', '+62 ').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
    return cleaned;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-[70]">
      <div className="bg-card w-full max-h-[90vh] rounded-t-3xl flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-background shadow-sm flex-shrink-0">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                {getActionIcon()}
              </div>
              <div>
                <h2 className="text-lg font-bold">{title}</h2>
                {customerName && (
                  <p className="text-sm text-muted-foreground">{customerName}</p>
                )}
                {phoneNumber && (
                  <p className="text-xs text-muted-foreground/75">{formatPhoneNumber(phoneNumber)}</p>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => insertMarkdown('**', '**')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title={t('notes.bold_shortcut')}
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => insertMarkdown('*', '*')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title={t('notes.italic_shortcut')}
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              onClick={() => insertMarkdown('- ', '')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title={t('notes.list_item')}
            >
              <List className="w-4 h-4" />
            </button>

            {/* Transcribe Button */}
            <div className="h-6 w-px bg-border mx-2" />
            <button
              onClick={handleToggleRecording}
              disabled={isTranscribing || !isTranscriptionSupported}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                !isTranscriptionSupported
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : isRecording
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : isTranscribing
                      ? 'bg-muted text-muted-foreground cursor-not-allowed'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
              title={
                !isTranscriptionSupported
                  ? compatibilityMessage || t('notes.transcription_not_supported')
                  : isRecording
                    ? t('notes.stop_recording')
                    : isTranscribing
                      ? t('notes.transcribing')
                      : t('notes.record_audio')
              }
            >
              {!isTranscriptionSupported ? (
                <>
                  <X className="w-4 h-4" />
                  <span>{t('notes.not_supported')}</span>
                </>
              ) : isTranscribing ? (
                <>
                  <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                  <span>{t('notes.transcribing')}</span>
                </>
              ) : isRecording ? (
                <>
                  <MicOff className="w-4 h-4" />
                  <span>{formatRecordingTime(recordingTime)}</span>
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4" />
                  <span>{t('notes.transcribe')}</span>
                </>
              )}
            </button>
          </div>

          <button
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              isPreviewMode
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {isPreviewMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="text-sm font-medium">
              {isPreviewMode ? t('notes.edit') : t('notes.preview')}
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 max-h-full overflow-y-auto">
          <div className="p-6">
            {/* Direct Contact Buttons - NEW FEATURE */}
            {!showActionSelection && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-muted-foreground">
                    {t('notes.quick_contact_label')}
                  </label>
                  <div className="flex items-center space-x-2">
                    {markedPhoneNumbers.length > 0 && (
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                        {t('notes.numbers_marked', { count: markedPhoneNumbers.length })}
                      </span>
                    )}
                    {markedActionType && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        markedActionType === 'whatsapp'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
                      }`}>
                        📞 {markedActionType === 'whatsapp' ? t('notes.whatsapp') : t('notes.call')} {t('notes.marked')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Show Quick Contact buttons if phone numbers available */}
                {customerPhoneNumbers && customerPhoneNumbers.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleDirectContact('whatsapp')}
                      className="flex items-center space-x-3 p-4 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 transition-colors"
                    >
                      <MessageCircle className="w-5 h-5 text-green-500" />
                      <div className="text-left">
                        <div className="font-medium">{t('notes.whatsapp')}</div>
                        <div className="text-xs opacity-75">
                          {customerPhoneNumbers.filter(p => p && p.trim()).length} {t('notes.numbers')}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDirectContact('call')}
                      className="flex items-center space-x-3 p-4 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 text-primary transition-colors"
                    >
                      <Phone className="w-5 h-5 text-primary" />
                      <div className="text-left">
                        <div className="font-medium">{t('notes.call')}</div>
                        <div className="text-xs opacity-75">
                          {customerPhoneNumbers.filter(p => p && p.trim()).length} {t('notes.numbers')}
                        </div>
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
                        <span className="text-xs text-amber-600 dark:text-amber-300">!</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                          {t('notes.no_phone_numbers_available')}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                          {t('notes.no_numbers_desc')}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => setMarkedActionType(markedActionType === 'whatsapp' ? null : 'whatsapp')}
                            className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-colors ${
                              markedActionType === 'whatsapp'
                                ? 'border-green-500 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                : 'border-green-200 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                            }`}
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {markedActionType === 'whatsapp' ? '✓ ' + t('notes.whatsapp') : t('notes.whatsapp')}
                            </span>
                          </button>
                          <button
                            onClick={() => setMarkedActionType(markedActionType === 'call' ? null : 'call')}
                            className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-colors ${
                              markedActionType === 'call'
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-primary/20 bg-white dark:bg-gray-800 text-primary hover:bg-primary/5'
                            }`}
                          >
                            <Phone className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {markedActionType === 'call' ? '✓ ' + t('notes.call') : t('notes.call')}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Action Selection */}
            {showActionSelection && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-muted-foreground mb-3">
                  {t('notes.choose_method')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleActionClick('whatsapp')}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors ${
                      selectedAction === 'whatsapp'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'border-border bg-background hover:bg-muted/50'
                    }`}
                  >
                    <MessageCircle className="w-5 h-5 text-green-500" />
                    <span className="font-medium">{t('notes.whatsapp')}</span>
                  </button>
                  <button
                    onClick={() => handleActionClick('call')}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors ${
                      selectedAction === 'call'
                        ? 'border-primary bg-secondary text-primary'
                        : 'border-border bg-background hover:bg-muted/50'
                    }`}
                  >
                    <Phone className="w-5 h-5 text-primary" />
                    <span className="font-medium">{t('notes.call')}</span>
                  </button>
                </div>
              </div>
            )}

            {isPreviewMode ? (
              <div className="prose prose-sm max-w-none">
                <div
                  className="min-h-[200px] p-4 bg-muted/50 rounded-lg border border-border"
                  dangerouslySetInnerHTML={{
                    __html: notes ? renderMarkdown(notes) : `<p class="text-muted-foreground">${t('notes.no_content_preview')}</p>`
                  }}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('notes.notes_details')}
                </label>
                <textarea
                  ref={textareaRef}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder || t('notes.placeholder')}
                  rows={10}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring resize-none text-sm bg-background text-foreground"
                />
                <div className="mt-2 text-xs text-muted-foreground">
                  <p>{t('notes.markdown_shortcuts')}</p>
                  <p>{t('notes.keyboard_shortcuts')}</p>
                </div>
              </div>
            )}

            {/* Progress Status Dropdown - Below Notes & Details */}
            {enableProgressSelection && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('notes.select_progress_status')}
                </label>
                <ProgressDropdown
                  value={convertToDropdownProgress(selectedProgress)}
                  onChange={(value) => setSelectedProgress(convertFromDropdownProgress(value))}
                  placeholder={t('notes.select_progress_status')}
                  className="w-full"
                />
              </div>
            )}

            {/* File Upload Section */}
            {enableFileUpload && (
              <div className="mt-6">
                <div className="mb-3">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {t('notes.attach_files')}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('notes.attach_files_desc')}
                  </p>
                </div>
                <FileUploadWithPreview
                  onFileUpload={handleFileUpload}
                  acceptedTypes="image/*,application/pdf,.doc,.docx,.txt,.xlsx,.xls"
                  maxFileSize={10}
                  maxFiles={5}
                  className="border border-border rounded-lg p-4"
                  resetKey={fileUploadResetKey}
                  enableMetadataExtraction={true}
                />
              </div>
            )}

            {/* Character count */}
            <div className="mt-4 text-right">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {uploadedFiles.length > 0 && t('notes.files_attached', { count: uploadedFiles.length })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('notes.characters_count', { count: notes.length })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t border-border bg-background">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-border text-muted-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors"
            >
              {t('notes.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!notes.trim() || (showActionSelection && !selectedAction)}
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>{showActionSelection ? t('notes.save_choose_action') : t('notes.save_continue')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Phone Number Selection Popup */}
      {showPhoneSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80]">
          <div className="bg-card w-[90%] max-w-md max-h-[80vh] rounded-2xl border border-border overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-border bg-background">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">{t('notes.select_phone_number')}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('notes.choose_number_to', { action: pendingActionType === 'whatsapp' ? t('notes.whatsapp') : t('notes.call').toLowerCase() })}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPhoneSelection(false);
                    setPendingActionType(null);
                  }}
                  className="p-2 hover:bg-muted/50 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Phone Numbers List */}
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              <div className="space-y-3">
                {customerPhoneNumbers
                  .filter(phone => phone && phone.trim().length > 0 && phone.trim() !== '-' && phone.trim() !== 'N/A')
                  .map((phoneNumber, index) => {
                    const isMarked = markedPhoneNumbers.includes(phoneNumber);
                    const formatPhoneForDisplay = (phone: string) => {
                      const cleaned = phone.replace(/[^\d+]/g, '');
                      if (cleaned.startsWith('+62')) {
                        return cleaned.replace('+62', '+62 ').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
                      }
                      return cleaned;
                    };

                    return (
                      <button
                        key={index}
                        onClick={() => handlePhoneSelection(phoneNumber)}
                        className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          isMarked
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/30'
                            : 'border-border bg-background hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            pendingActionType === 'whatsapp' ? 'bg-green-500' : 'bg-primary'
                          }`} />
                          <div className="text-left">
                            <div className="font-medium">
                              {formatPhoneForDisplay(phoneNumber)}
                            </div>
                            {phoneNumber.includes('WhatsApp') && (
                              <div className="text-xs text-muted-foreground">{t('notes.whatsapp')}</div>
                            )}
                          </div>
                        </div>
                        {isMarked && (
                          <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                            ✓ {t('notes.marked')}
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>

              {customerPhoneNumbers.filter(phone =>
                phone && phone.trim().length > 0 && phone.trim() !== '-' && phone.trim() !== 'N/A'
              ).length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted/50 rounded-full flex items-center justify-center">
                    <Phone className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">{t('notes.no_valid_phone_numbers_available')}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border bg-background">
              <button
                onClick={() => {
                  setShowPhoneSelection(false);
                  setPendingActionType(null);
                }}
                className="w-full px-6 py-3 border border-border text-muted-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors"
              >
                {t('notes.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
